import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TenderCard = ({ tender, onViewDetails, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'bg-primary/10 text-primary border border-primary/20';
      case 'submitted': return 'bg-accent/10 text-accent border border-accent/20';
      case 'under evaluation': return 'bg-warning/10 text-warning border border-warning/20';
      case 'won': return 'bg-success/10 text-success border border-success/20';
      case 'lost': return 'bg-error/10 text-error border border-error/20';
      default: return 'bg-muted text-muted-foreground border border-border';
    }
  };

  const getUrgencyColor = (daysLeft) => {
    if (daysLeft <= 3) return 'text-error bg-error/10 border border-error/20';
    if (daysLeft <= 7) return 'text-warning bg-warning/10 border border-warning/20';
    return 'text-muted-foreground bg-muted border border-border';
  };

  const handleViewProfile = (e) => {
    e?.stopPropagation();
    navigate(`/engagement-management-hub/tender/${tender?.id}`, { state: { tender } });
  };

  const handleEdit = (e) => {
    e?.stopPropagation();
    onEdit?.(tender);
  };

  const handleDeleteClick = (e) => {
    e?.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = (e) => {
    e?.stopPropagation();
    setShowDeleteConfirm(false);
    onDelete?.(tender);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 relative flex flex-col">
      {/* Header Row: Title + Action Icons */}
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base font-bold text-foreground leading-snug pr-3 flex-1 min-w-0">
          {tender?.name}
        </h3>
        {/* Action Icons — compact, right-aligned */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleViewProfile}
            title="View Profile"
            className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-primary/10 transition-all"
          >
            <Icon name="ExternalLink" size={15} />
          </button>
          <button
            onClick={handleEdit}
            title="Edit"
            className="p-1.5 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-all"
          >
            <Icon name="Pencil" size={15} />
          </button>
          <button
            onClick={handleDeleteClick}
            title="Delete"
            className="p-1.5 rounded-md text-slate-400 hover:text-error hover:bg-error/10 transition-all"
          >
            <Icon name="Trash2" size={15} />
          </button>
        </div>
      </div>

      {/* Status + Urgency pills */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getStatusColor(tender?.status)}`}>
          {tender?.status}
        </span>
        {tender?.daysLeft !== null && tender?.daysLeft !== undefined && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${getUrgencyColor(tender?.daysLeft)}`}>
            <Icon name="Clock" size={11} />
            {tender?.daysLeft}d left
          </span>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-border/60 mb-4" />

      {/* Data Rows */}
      <div className="space-y-2.5 mb-4 flex-1">
        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Icon name="Building2" size={15} className="flex-shrink-0 text-slate-400" />
          <span className="truncate font-medium text-foreground/80">{tender?.client}</span>
        </div>
        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Icon name="DollarSign" size={15} className="flex-shrink-0 text-slate-400" />
          <span className="font-semibold text-foreground">{tender?.value}</span>
        </div>
        <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
          <Icon name="Calendar" size={15} className="flex-shrink-0 text-slate-400" />
          <span>Deadline: {tender?.deadline}</span>
        </div>
      </div>

      {/* Technical & Financial Roles */}
      <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-slate-50 rounded-lg border border-border/50">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Technical Role</p>
          <p className="text-sm font-semibold text-foreground">{tender?.technicalRole}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Financial Role</p>
          <p className="text-sm font-semibold text-foreground">{tender?.financialRole}</p>
        </div>
      </div>

      {/* CTA */}
      <Button
        variant="outline"
        size="sm"
        iconName="Eye"
        iconPosition="left"
        iconSize={15}
        onClick={() => onViewDetails?.(tender)}
        fullWidth
      >
        View Details
      </Button>

      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <div
          className="absolute inset-0 bg-white/96 backdrop-blur-sm rounded-xl z-10 flex flex-col items-center justify-center gap-3 p-6"
          onClick={(e) => e?.stopPropagation()}
        >
          <div className="p-3 bg-error/10 rounded-full">
            <Icon name="Trash2" size={22} className="text-error" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-foreground mb-1">Delete Tender?</p>
            <p className="text-xs text-muted-foreground">"{tender?.name}" will be permanently removed.</p>
          </div>
          <div className="flex gap-2 w-full">
            <button
              onClick={(e) => { e?.stopPropagation(); setShowDeleteConfirm(false); }}
              className="flex-1 px-3 py-2 text-xs font-medium border border-border rounded-lg hover:bg-muted transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="flex-1 px-3 py-2 text-xs font-medium bg-error text-white rounded-lg hover:bg-error/90 transition-all"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenderCard;